import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../lib/api';

interface CoverSectionProps {
  restUrl: string;
  coverKey: string | null;
  bookTitle: string;
  authorName: string;
  onCoverUpdated: (newCoverKey: string) => void;
}

type CoverStatus = 'idle' | 'generating' | 'uploading' | 'validating' | 'done' | 'error';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120000;

export function CoverSection({ restUrl, coverKey, bookTitle, authorName, onCoverUpdated }: CoverSectionProps) {
  const [status, setStatus] = useState<CoverStatus>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);

  const clearPoll = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Fetch preview URL when coverKey is set
  useEffect(() => {
    if (!coverKey) {
      setPreviewUrl(null);
      return;
    }
    fetchWithAuth(`${restUrl}cover/preview-url?key=${encodeURIComponent(coverKey)}`)
      .then(async res => {
        if (res.ok) {
          const data = await res.json() as { url: string };
          setPreviewUrl(data.url);
        }
      })
      .catch(() => {
        // Fallback: try to construct a direct URL if preview-url endpoint doesn't exist yet
        // The cover key is used as-is; CloudFront distribution or presigned URL would be needed
        setPreviewUrl(null);
      });
  }, [coverKey, restUrl]);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => clearPoll();
  }, [clearPoll]);

  const startPolling = useCallback((id: string) => {
    clearPoll();
    pollStartRef.current = Date.now();

    pollIntervalRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        clearPoll();
        setStatus('error');
        setErrorMsg('Cover generation timed out — please try again');
        return;
      }

      try {
        const res = await fetchWithAuth(`${restUrl}cover/status/${id}`);
        if (!res.ok) return;
        const data = await res.json() as { status?: string; coverKey?: string; error?: string };
        if (data.status === 'complete' && data.coverKey) {
          clearPoll();
          setStatus('done');
          onCoverUpdated(data.coverKey);
        } else if (data.status === 'error') {
          clearPoll();
          setStatus('error');
          setErrorMsg('Cover generation failed — try again');
        }
      } catch {
        // Polling network error — keep trying until timeout
      }
    }, POLL_INTERVAL_MS);
  }, [restUrl, onCoverUpdated, clearPoll]);

  const handleGenerate = async () => {
    setStatus('generating');
    setErrorMsg(null);
    try {
      const res = await fetchWithAuth(`${restUrl}cover/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageCount: 200, bookTitle, authorName }),
      });
      if (!res.ok) throw new Error('Generate request failed');
      const data = await res.json() as { jobId: string };
      setJobId(data.jobId);
      startPolling(data.jobId);
    } catch {
      setStatus('error');
      setErrorMsg('Failed to start cover generation — try again');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';

    if (!file) return;

    // Client-side file type check
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setErrorMsg('Only JPG or PNG files are accepted');
      setStatus('error');
      return;
    }

    setErrorMsg(null);
    setStatus('uploading');

    try {
      // Step 1: Get presigned upload URL
      const urlRes = await fetchWithAuth(`${restUrl}cover/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: file.type }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, uploadKey } = await urlRes.json() as { uploadUrl: string; uploadKey: string };

      // Step 2: Upload file directly to S3
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) throw new Error('Upload to S3 failed');

      // Step 3: Confirm and validate
      setStatus('validating');
      const confirmRes = await fetchWithAuth(`${restUrl}cover/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadKey }),
      });

      if (confirmRes.status === 422) {
        const errData = await confirmRes.json() as { field: string; message: string };
        setErrorMsg(errData.message);
        setStatus('error');
        return;
      }

      if (!confirmRes.ok) throw new Error('Confirm request failed');
      const confirmData = await confirmRes.json() as { coverKey: string };
      setStatus('done');
      onCoverUpdated(confirmData.coverKey);
    } catch (err) {
      if (status !== 'error') {
        setStatus('error');
        setErrorMsg('Upload failed — please try again');
      }
    }
  };

  const isProcessing = status === 'generating' || status === 'uploading' || status === 'validating';

  const statusLabel =
    status === 'generating' ? 'Generating cover…' :
    status === 'uploading'  ? 'Uploading…' :
    status === 'validating' ? 'Validating…' :
    null;

  void jobId;

  return (
    <div className="max-w-xl mx-auto px-8 py-10">
      <h2 className="text-lg font-semibold text-stone-800 mb-1">Cover</h2>
      <p className="text-sm text-stone-500 mb-6">
        Generate an AI-designed cover or upload your own photo.
      </p>

      {/* Cover preview */}
      {(coverKey || status === 'done') && previewUrl && (
        <div className="mb-6">
          <div className="aspect-[6.25/9.25] w-48 rounded overflow-hidden shadow-md border border-stone-200">
            <img src={previewUrl} className="w-full h-full object-cover" alt="Book cover" />
          </div>
          <p className="text-xs text-green-700 mt-2">Cover saved</p>
        </div>
      )}

      {/* Loading state */}
      {isProcessing && (
        <div className="flex items-center gap-3 mb-6 text-stone-600">
          <svg className="w-5 h-5 animate-spin text-amber-600" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">{statusLabel}</span>
        </div>
      )}

      {/* Action buttons */}
      {!isProcessing && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={isProcessing}
            className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            Generate cover
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            className="px-4 py-2 bg-white border border-stone-300 text-stone-700 text-sm font-medium rounded hover:bg-stone-50 disabled:opacity-50 transition-colors"
          >
            Upload your own
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={e => void handleFileChange(e)}
      />

      {/* Error display */}
      {status === 'error' && errorMsg && (
        <p className="text-red-600 text-sm mt-2">{errorMsg}</p>
      )}
    </div>
  );
}
